import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { CallStatus } from '../../../../lib/enums/call-status.enum';
import { CurrencyEnum, CurrencySymbolEnum } from '../../../../lib/enums/currency.enum';
import lang from '../../../../lib/language';
import { RootState } from '../../../../lib/store';
import { PaymentCall } from '../../../../lib/store/payment/types';
import { AvailableCashElement } from '../../../../lib/store/reporting/types';
import { getWalletTotalCalculatedCash, isUserVerifiedStatus } from '../../../../lib/store/selectors';
import { formatCurrencyBySymbol } from '../../../../lib/util/DataHelpers';
import { isCallStatusUIDone } from '../../../../lib/util/error-handling/StatusByCallHelpers';
import { CurrenciesSwitch } from '../../../../lib/util/types';

import IconArrow from '../../../../assets/img/wallet/icon-down.svg';
import EUFlag from '../../../../assets/img/wallet/icon-eu-flag.svg';
import TotalValueIcon from '../../../../assets/img/wallet/icon-total-cash.svg';
import USFlag from '../../../../assets/img/wallet/icon-us-flag.svg';
import { ClickableImage, Preloader } from '../../../../components/index';
import useIsMobile from '../../../../hooks/useIsMobile';
import CurrencyDropDown from '../CurrencyDropDown/CurrencyDropDown';
import CurrencyOptions from '../CurrencyOptions/CurrencyOptions';

import styles from './CurrencyHeader.module.scss';

type Props = {
    currentAvailbleCash: number;
    setCurrentAvailableCash: (val: number) => void;
}
const CurrencyHeader = ({ currentAvailbleCash, setCurrentAvailableCash }: Props) => {
  const calculatedCash = useSelector<RootState, number>((state: RootState) => getWalletTotalCalculatedCash(state));
  const availableCash = useSelector<RootState, AvailableCashElement[]>(
    (state: RootState) => state.payment.availableCashInfo.availableCash[0].availableCashMultiCurrency,
  );
  const isUserVerified = useSelector<RootState, boolean>(
    (state: RootState) => isUserVerifiedStatus(state.crm.individualExtendedInfo),
  );
  const isCashLoaded = useSelector<RootState, boolean>(
    (state: RootState) => isCallStatusUIDone(PaymentCall.cashCalculated, state.payment.statusByCall),
  );
  const isPaymentsAvailableCashReady = useSelector<RootState, boolean>(
    (state: RootState) => state.payment.statusByCall[PaymentCall.getAvailableCash]?.status === CallStatus.READY,
  );
  const [currentCurrency, setCurrentCurrency] = useState<CurrenciesSwitch>(CurrencyEnum.EUR);
  const [labelText, setLabelText] = useState<string>(lang.mobileFundsScreenAvailableCash());
  const [showArrowIcon, setShowArrowicon] = useState<boolean>(false);
  const [showAllCurrencies, setShowAllCurrencies] = useState<boolean>(false);
  const [showCurrencyPopUp, setShowCurrencyPopUp] = useState<boolean>(false);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const isMobile = useIsMobile();

  useEffect(() => {
    if (availableCash.length === 1) {
      setCurrentCurrency(availableCash[0].currency as CurrenciesSwitch);
      setCurrentAvailableCash(availableCash[0].availableCash);
    } else if (availableCash.length > 1) {
      setCurrentCurrency(CurrencyEnum.EUR);
      setShowArrowicon(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableCash]);

  useEffect(() => {
    if (currentCurrency === 'ALL') {
      setLabelText(lang.fundScreenTotalAvailableCash());
      setCurrentAvailableCash(calculatedCash);
    } else {
      setCurrentAvailableCash(availableCash.find(el => el.currency === currentCurrency)?.availableCash ?? 0);
      setLabelText(lang.mobileFundsScreenAvailableCash());
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableCash, calculatedCash, currentCurrency]);

  const renderIcon = () => {
    if (currentCurrency === CurrencyEnum.USD) {
      return <img src={USFlag} alt="usd" />;
    }
    if (currentCurrency === CurrencyEnum.EUR) {
      return <img src={EUFlag} alt="eur" />;
    }
    return <img src={TotalValueIcon} alt="total" />;
  };

  const handleAvailableCash = () => {
    const currencySymbol = currentCurrency === CurrencyEnum.EUR ? CurrencySymbolEnum.EURO : CurrencySymbolEnum.USD;
    if (!isUserVerified) {
      return <h4 id="fundsAvailableCash" className={styles.statusMoney}>{formatCurrencyBySymbol(0, currencySymbol)}</h4>;
    }
    if ((!isUserVerified || isCashLoaded) && isPaymentsAvailableCashReady) {
      return <h4 id="fundsAvailableCash" className={styles.statusMoney}>{formatCurrencyBySymbol(currentAvailbleCash, currencySymbol)}</h4>;
    }
    return <div className={styles.loaderWrapper}><Preloader owner="Funds" text={''} /></div>;
  };

  const toggleCurrencyDropDown = (e) => {
    if (isMobile) {
    document.querySelector('body')!.style.overflow = 'hidden';
    setShowAllCurrencies(true);
    } else {
      setAnchor(e.currentTarget);
      setShowCurrencyPopUp(!showCurrencyPopUp);
    }
  };

  const handleOpitionsClose = () => {
    setShowAllCurrencies(false);
    document.querySelector('body')!.style.overflow = 'auto';
  };

  return (
    <div>
      <span className={(isMobile ? styles.availableCashTextMob : styles.availableCashTextWeb)}>{labelText}</span>
      <div className={styles.availableCash}>
        <div className={styles.iconWrapper}>{renderIcon()}</div>
        <div className={styles.availableCashValue}>{handleAvailableCash()}</div>
        {showArrowIcon && (
        <ClickableImage
          className={styles.arrowDown}
          src={IconArrow}
          alt="open available currencies"
          width="24px"
          onClick={toggleCurrencyDropDown}
        />
        )}
        {showAllCurrencies && (
        <CurrencyOptions
          activeCurrency={currentCurrency}
          handleClose={handleOpitionsClose}
          chooseCurrency={(val: CurrenciesSwitch) => setCurrentCurrency(val)}
        />
        )}
        <CurrencyDropDown
          isOpen={showCurrencyPopUp}
          anchorEl={anchor}
          handleClose={() => setShowCurrencyPopUp(false)}
          chooseCurrency={(val: CurrenciesSwitch) => setCurrentCurrency(val)}
        />
      </div>
    </div>
  );
};
export default CurrencyHeader;
